export interface EventItem {
  id: number;
  title: string;
  subtitle: string | null;
  description: string | null;
  image_url: string | null;
  logo_url?: string | null;
  /** @deprecated Use event_date_from / event_date_to instead */
  event_date: string | null;
  event_date_from?: string | null;
  event_date_to?: string | null;
  location: string | null;
  category: string | null;
  is_featured: boolean | number | null;
  register_url: string | null;
  venue_description?: string | null;
  map_embed_url?: string | null;
  hotel_name?: string | null;
  phone?: string | null;
  address?: string | null;
  directions_link?: string | null;
  directions?: string | null;
  venue_image_url?: string | null;
  created_at?: string;
}

export type EventStatusFilter = "all" | "upcoming" | "past";
export type EventSortOption = "date-asc" | "date-desc" | "title-asc" | "title-desc" | "newest";

export interface EventFilters {
  search: string;
  location: string;
  year: string;
  category: string;
  status: EventStatusFilter;
  featuredOnly: boolean;
  sort: EventSortOption;
}

export const DEFAULT_EVENT_FILTERS: EventFilters = {
  search: "",
  location: "all",
  year: "all",
  category: "all",
  status: "all",
  featuredOnly: false,
  sort: "date-asc",
};

const MONTHS: Record<string, number> = {
  january: 0, jan: 0,
  february: 1, feb: 1,
  march: 2, mar: 2,
  april: 3, apr: 3,
  may: 4,
  june: 5, jun: 5,
  july: 6, jul: 6,
  august: 7, aug: 7,
  september: 8, sep: 8, sept: 8,
  october: 9, oct: 9,
  november: 10, nov: 10,
  december: 11, dec: 11,
};

export function parseEventDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr?.trim()) return null;

  const direct = Date.parse(dateStr);
  if (!Number.isNaN(direct)) return new Date(direct);

  const match = dateStr.match(/(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]+)\s+(\d{4})/i);
  if (match) {
    const day = parseInt(match[1], 10);
    const month = MONTHS[match[2].toLowerCase()];
    const year = parseInt(match[3], 10);
    if (month !== undefined) return new Date(year, month, day);
  }

  const yearMatch = dateStr.match(/(\d{4})/);
  if (yearMatch) return new Date(parseInt(yearMatch[1], 10), 0, 1);

  return null;
}

export function getEventYear(dateStr: string | null | undefined): string | null {
  const parsed = parseEventDate(dateStr);
  return parsed ? String(parsed.getFullYear()) : null;
}

/**
 * Returns "upcoming" if the event end date (event_date_to) is today or in the future.
 * Falls back to event_date_from, then the legacy event_date.
 */
export function getEventStatusRange(
  event: { event_date_from?: string | null; event_date_to?: string | null; event_date?: string | null },
): "upcoming" | "past" | "unknown" {
  const endDate = parseEventDate(event.event_date_to ?? event.event_date_from ?? event.event_date);
  if (!endDate) return "unknown";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  return endDate >= today ? "upcoming" : "past";
}

/** @deprecated Prefer getEventStatusRange for range-aware logic */
export function getEventStatus(dateStr: string | null | undefined): "upcoming" | "past" | "unknown" {
  const parsed = parseEventDate(dateStr);
  if (!parsed) return "unknown";

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDay = new Date(parsed);
  eventDay.setHours(0, 0, 0, 0);

  return eventDay >= today ? "upcoming" : "past";
}

/**
 * Formats a date range as "25-29 November, 2026".
 * - Same month+year  → "25-29 November, 2026"
 * - Different months, same year → "25 November – 2 December, 2026"
 * - Different years  → "30 December, 2025 – 2 January, 2026"
 * - Single date (no range / same day) → "25 November, 2026"
 */
export function formatEventDateRange(
  event: { event_date_from?: string | null; event_date_to?: string | null; event_date?: string | null },
): string {
  const from = parseEventDate(event.event_date_from ?? event.event_date);
  const to = parseEventDate(event.event_date_to ?? event.event_date_from ?? event.event_date);

  if (!from) return "";

  const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const fromDay = from.getDate();
  const fromMonth = MONTH_NAMES[from.getMonth()];
  const fromYear = from.getFullYear();

  if (!to || from.getTime() === to.getTime()) {
    return `${fromDay} ${fromMonth}, ${fromYear}`;
  }

  const toDay = to.getDate();
  const toMonth = MONTH_NAMES[to.getMonth()];
  const toYear = to.getFullYear();

  if (fromYear === toYear && from.getMonth() === to.getMonth()) {
    // Same month & year: "25-29 November, 2026"
    return `${fromDay}-${toDay} ${fromMonth}, ${fromYear}`;
  }

  if (fromYear === toYear) {
    // Same year, different months: "25 November – 2 December, 2026"
    return `${fromDay} ${fromMonth} – ${toDay} ${toMonth}, ${fromYear}`;
  }

  // Different years: "30 December, 2025 – 2 January, 2026"
  return `${fromDay} ${fromMonth}, ${fromYear} – ${toDay} ${toMonth}, ${toYear}`;
}

export function resolveEventImageUrl(imageUrl: string | null | undefined): string {
  if (!imageUrl) return "/assets/images/banner1.jpg";
  if (imageUrl.startsWith("http") || imageUrl.startsWith("/assets/")) return imageUrl;
  const base = import.meta.env.VITE_API_URL || "http://localhost:3001";
  return `${base}${imageUrl}`;
}

/** Speaker photos may live in frontend /assets or on the API upload host. */
export function resolveSpeakerImageUrl(imageUrl: string | null | undefined): string | null {
  if (!imageUrl) return null;
  if (imageUrl.startsWith("http") || imageUrl.startsWith("/assets/")) return imageUrl;
  const base = import.meta.env.VITE_API_URL || "http://localhost:3001";
  return `${base}${imageUrl}`;
}

export function resolveEventLogoUrl(logoUrl: string | null | undefined): string | null {
  if (!logoUrl) return null;
  if (logoUrl.startsWith("http")) return logoUrl;
  const base = import.meta.env.VITE_API_URL || "http://localhost:3001";
  return `${base}${logoUrl}`;
}

/** Convert any stored date string into YYYY-MM-DD for <input type="date"> */
export function toDateInputValue(dateStr: string | null | undefined): string {
  const parsed = parseEventDate(dateStr);
  if (!parsed) return "";
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Display-friendly date for the public site */
export function formatEventDate(dateStr: string | null | undefined): string {
  if (!dateStr?.trim()) return "";
  if (!/^\d{4}-\d{2}-\d{2}/.test(dateStr.trim())) return dateStr.trim();
  const parsed = parseEventDate(dateStr);
  if (!parsed) return dateStr;
  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function eventLabel(event: EventItem) {
  return [formatEventDateRange(event), event.location].filter(Boolean).join(" · ");
}

export function extractUniqueValues(events: EventItem[], field: "location" | "category"): string[] {
  const values = new Set<string>();
  for (const event of events) {
    const value = event[field];
    if (value?.trim()) values.add(value.trim());
  }
  return Array.from(values).sort((a, b) => a.localeCompare(b));
}

export function extractYears(events: EventItem[]): string[] {
  const years = new Set<string>();
  for (const event of events) {
    // Prefer event_date_from for year extraction; fall back to legacy event_date
    const year = getEventYear(event.event_date_from ?? event.event_date);
    if (year) years.add(year);
  }
  return Array.from(years).sort((a, b) => b.localeCompare(a));
}

export function filterAndSortEvents(events: EventItem[], filters: EventFilters): EventItem[] {
  let result = [...events];

  if (filters.search.trim()) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (e) =>
        e.title.toLowerCase().includes(q) ||
        e.subtitle?.toLowerCase().includes(q) ||
        e.description?.toLowerCase().includes(q) ||
        e.location?.toLowerCase().includes(q) ||
        e.category?.toLowerCase().includes(q),
    );
  }

  if (filters.location !== "all") {
    result = result.filter((e) => e.location === filters.location);
  }

  if (filters.year !== "all") {
    result = result.filter((e) => getEventYear(e.event_date_from ?? e.event_date) === filters.year);
  }

  if (filters.category !== "all") {
    result = result.filter((e) => e.category === filters.category);
  }

  if (filters.status !== "all") {
    result = result.filter((e) => getEventStatusRange(e) === filters.status);
  }

  if (filters.featuredOnly) {
    result = result.filter((e) => Boolean(e.is_featured));
  }

  result.sort((a, b) => {
    switch (filters.sort) {
      case "title-asc":
        return a.title.localeCompare(b.title);
      case "title-desc":
        return b.title.localeCompare(a.title);
      case "newest":
        return (b.id ?? 0) - (a.id ?? 0);
      case "date-desc": {
        const da = parseEventDate(a.event_date_from ?? a.event_date)?.getTime() ?? 0;
        const db = parseEventDate(b.event_date_from ?? b.event_date)?.getTime() ?? 0;
        return db - da;
      }
      case "date-asc":
      default: {
        const da = parseEventDate(a.event_date_from ?? a.event_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const db = parseEventDate(b.event_date_from ?? b.event_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        return da - db;
      }
    }
  });

  return result;
}

export function getHeroEvents(events: EventItem[]): EventItem[] {
  // Only return upcoming events — never surface past events on the homepage hero
  return events
    .filter((event) => getEventStatusRange(event) === "upcoming")
    .sort((a, b) => {
      const aTime = parseEventDate(a.event_date_from ?? a.event_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const bTime = parseEventDate(b.event_date_from ?? b.event_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      return aTime - bTime;
    });
}

/** Single event for the homepage hero. Only returns upcoming events — past events are never shown as the hero. */
export function getCurrentEvent(
  events: EventItem[],
  selectedEventId?: number | null,
): EventItem | null {
  if (selectedEventId != null) {
    const selected = events.find((event) => event.id === selectedEventId);
    // Only show the selected event if it is upcoming
    if (selected && getEventStatusRange(selected) === "upcoming") return selected;
  }
  return getHeroEvents(events)[0] ?? null;
}

export function getUpcomingEvents(
  events: EventItem[],
  excludeId?: number | null,
): EventItem[] {
  return events
    .filter(
      (event) =>
        getEventStatusRange(event) === "upcoming" &&
        (excludeId == null || event.id !== excludeId),
    )
    .sort((a, b) => {
      const aTime = parseEventDate(a.event_date_from ?? a.event_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const bTime = parseEventDate(b.event_date_from ?? b.event_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      return aTime - bTime;
    });
}

export function getPastEvents(
  events: EventItem[],
  excludeId?: number | null,
): EventItem[] {
  return events
    .filter(
      (event) =>
        getEventStatusRange(event) === "past" &&
        (excludeId == null || event.id !== excludeId),
    )
    .sort(
      (a, b) =>
        (parseEventDate(b.event_date_from ?? b.event_date)?.getTime() ?? 0) -
        (parseEventDate(a.event_date_from ?? a.event_date)?.getTime() ?? 0),
    );
}

export function getArchiveEvents(events: EventItem[]): EventItem[] {
  const heroIds = new Set(getHeroEvents(events).map((event) => event.id));
  return events
    .filter((event) => !heroIds.has(event.id))
    .sort((a, b) => (parseEventDate(b.event_date_from ?? b.event_date)?.getTime() ?? 0) - (parseEventDate(a.event_date_from ?? a.event_date)?.getTime() ?? 0));
}

export function countActiveFilters(filters: EventFilters): number {
  let count = 0;
  if (filters.search.trim()) count++;
  if (filters.location !== "all") count++;
  if (filters.year !== "all") count++;
  if (filters.category !== "all") count++;
  if (filters.status !== "all") count++;
  if (filters.featuredOnly) count++;
  if (filters.sort !== DEFAULT_EVENT_FILTERS.sort) count++;
  return count;
}
